import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  addFavorite,
  fetchFavoriteStatus,
  removeFavorite,
} from '../../../util/api';

import { IconHeart, Modal, NamedLink } from '../../../components';

import css from './FavoriteButton.module.css';

const LockIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="m5 12 4 4L19 6" />
  </svg>
);

/**
 * FavoriteButton renders the listing-gallery favorite control and its feedback modals.
 *
 * @param {Object} props component props
 * @param {Object} props.listing Sharetribe listing entity
 * @param {boolean} props.isAuthenticated whether the current visitor is signed in
 * @param {Function} props.onManageDisableScrolling modal scroll-management callback
 * @returns {JSX.Element|null} favorite control for non-owner listing pages
 */
const FavoriteButton = props => {
  const { listing, isAuthenticated, onManageDisableScrolling } = props;
  const intl = useIntl();
  const location = useLocation();
  const listingId = listing?.id?.uuid;
  const listingTitle = listing?.attributes?.title || '';

  const [isFavorite, setIsFavorite] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [feedback, setFeedback] = useState('default');
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setError(null);
    setFeedback('default');

    if (!isAuthenticated || !listingId) {
      setIsFavorite(false);
      return () => {
        active = false;
      };
    }

    fetchFavoriteStatus(listingId)
      .then(response => {
        if (active) setIsFavorite(Boolean(response?.isFavorite));
      })
      .catch(() => {
        if (active) setError('status');
      });

    return () => {
      active = false;
    };
  }, [isAuthenticated, listingId]);

  const from = `${location.pathname}${location.search}${location.hash}`;
  const tooltipMessageId = error
    ? 'FavoriteButton.error'
    : feedback === 'removed'
      ? 'FavoriteButton.removedTooltip'
      : isFavorite
        ? 'FavoriteButton.savedTooltip'
        : 'FavoriteButton.addTooltip';
  const ariaLabel = intl.formatMessage({
    id: isFavorite ? 'FavoriteButton.removeAriaLabel' : 'FavoriteButton.addAriaLabel',
  });

  const handleToggle = async () => {
    if (!isAuthenticated) {
      setAuthModalOpen(true);
      return;
    }

    if (!listingId || inProgress) return;

    setInProgress(true);
    setError(null);
    try {
      if (isFavorite) {
        await removeFavorite(listingId);
        setIsFavorite(false);
        setFeedback('removed');
      } else {
        await addFavorite(listingId, intl.locale);
        setIsFavorite(true);
        setFeedback('saved');
        setSuccessModalOpen(true);
      }
    } catch (requestError) {
      setError('toggle');
    } finally {
      setInProgress(false);
    }
  };

  return (
    <>
      <div className={css.control}>
        <button
          type="button"
          className={classNames(css.heartButton, { [css.heartButtonSaved]: isFavorite })}
          onClick={handleToggle}
          disabled={inProgress}
          aria-label={ariaLabel}
          aria-pressed={isFavorite}
          aria-busy={inProgress}
        >
          {inProgress ? (
            <span className={css.heartSpinner} aria-hidden="true" />
          ) : (
            <IconHeart size={24} filled={isFavorite} />
          )}
        </button>
        <div className={css.tooltip} role="status" aria-live="polite">
          <FormattedMessage id={tooltipMessageId} />
        </div>
      </div>

      <Modal
        id="FavoriteAuthenticationModal"
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        contentClassName={css.modalContent}
        usePortal
      >
        <div className={css.modalIconLocked}>
          <LockIcon className={css.modalIconSvg} />
        </div>
        <h2 className={css.modalTitle}>
          <FormattedMessage id="FavoriteAuthModal.title" />
        </h2>
        <p className={css.modalMessage}>
          <FormattedMessage id="FavoriteAuthModal.message" />
        </p>
        <div className={css.modalActions}>
          <NamedLink
            name="LoginPage"
            className={css.primaryAction}
            to={{ state: { from } }}
          >
            <FormattedMessage id="FavoriteAuthModal.login" />
          </NamedLink>
          <NamedLink
            name="SignupPage"
            className={css.secondaryAction}
            to={{ state: { from } }}
          >
            <FormattedMessage id="FavoriteAuthModal.register" />
          </NamedLink>
        </div>
        <p className={css.modalHelper}>
          <FormattedMessage id="FavoriteAuthModal.returnMessage" />
        </p>
      </Modal>

      <Modal
        id="FavoriteSuccessModal"
        isOpen={successModalOpen}
        onClose={() => setSuccessModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        contentClassName={css.modalContent}
        usePortal
      >
        <div className={css.modalIconSuccess}>
          <CheckIcon className={css.modalIconSvg} />
        </div>
        <h2 className={css.modalTitle}>
          <FormattedMessage id="FavoriteSuccessModal.title" />
        </h2>
        <p className={css.modalMessage}>
          <FormattedMessage
            id="FavoriteSuccessModal.message"
            values={{ listingTitle: <strong>{listingTitle}</strong> }}
          />
        </p>
        <NamedLink
          name="InboxPage"
          params={{ tab: 'favorites' }}
          className={css.favoriteListLink}
          onClick={() => setSuccessModalOpen(false)}
        >
          <FormattedMessage id="FavoriteSuccessModal.viewFavorites" />
        </NamedLink>
      </Modal>
    </>
  );
};

export default FavoriteButton;
